import isEqual from 'lodash/isEqual';
import { watch } from 'vue';
import store from '../store';
import { addUIFields, omitUIFields } from './action-types';
import { sortActionsTypes } from '@/util/workflowDetails';

let acceptUpdates = false;

export const workflowKeyPrefix = 'workflow:';
watch(
  () => store.state.workflows.details,
  (newState) => {
    const workflowId = newState?.workflow?.id;
    if (workflowId && acceptUpdates) {
      const workflowState = extractWorkflowState(newState);
      saveState(`${workflowKeyPrefix}${workflowId}`, workflowState);
    }
  },
  { immediate: true, deep: true },
);

function extractWorkflowState(state) {
  if (typeof omitUIFields !== 'function') {
    console.error(
      '[saveAndLoadState] omitUIFields is not a function!',
      typeof omitUIFields,
    );
  }

  const cleanedNodes = state.nodes ? omitUIFields(state.nodes) : [];

  return {
    workflow: state.workflow,
    credentials: state.credentials,
    startSettings: state.startSettings,
    nodes: cleanedNodes,
    published: state.workflow?.published,
    alertSettings: state.workflow?.alertSettings,
    s3StorageEnabled: state.s3StorageEnabled,
    selectedWorkflowVersion: state.selectedWorkflowVersion,
    selectedCommit: state.selectedCommit,
  };
}

export function overwriteWorkflowState(key) {
  try {
    const workflowState = loadState(key);
    if (!workflowState?.workflow) {
      return;
    }
    const prefix = 'workflows/details';
    const published = workflowState.workflow.published;
    const alertSettings = workflowState.workflow.alertSettings;
    const s3StorageEnabled =
      workflowState.s3StorageEnabled ??
      workflowState.workflow.IOStorageType === 's3';
    const version = workflowState.selectedWorkflowVersion;
    const commit = workflowState.selectedCommit;

    store.commit(`${prefix}/SET_AUTOMATION_WORKFLOW`, workflowState.workflow);
    store.commit(`${prefix}/SET_WORKFLOW_SELECT_VERSION`, published);
    store.commit(`${prefix}/SET_ALERT_SETTINGS`, alertSettings);
    store.commit(`${prefix}/SET_S3_STORAGE_ENABLED`, s3StorageEnabled);
    store.commit(`${prefix}/SET_CREDENTIALS`, workflowState.credentials);
    store.commit(`${prefix}/SET_START_SETTINGS`, workflowState.startSettings);
    const actionTypes = sortActionsTypes('', workflowState.startSettings.trigger_type);
    const nodes = addUIFields(workflowState.nodes, actionTypes);
    store.commit(`${prefix}/SET_NODES`, nodes);
    store.commit(`${prefix}/SET_WORKFLOW_SELECT_VERSION`, version);
    store.commit(`${prefix}/SET_SELECTED_COMMIT`, commit);
  } catch {
    // ignore
  } finally {
    acceptUpdates = true;
  }
}

function loadState(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (error) {
    return null;
  }
}

export function compareLocalState(key) {
  try {
    const savedState = loadState(key);
    if (!savedState?.workflow) {
      return false;
    }
    const currentState = JSON.parse(
      JSON.stringify(
        extractWorkflowState(store.state.workflows.details),
        getCircularReplacer(),
      ),
    );
    return !isEqual(currentState, savedState);
  } catch (error) {
    console.error('Error comparing states:', error);
    return false;
  }
}

export function clearLocalState(key) {
  localStorage.removeItem(key);
  const cleanState = extractWorkflowState(store.state.workflows.details);
  saveState(key, cleanState);
}

export function enableStateUpdates() {
  acceptUpdates = true;
}

function getCircularReplacer() {
  const seen = new WeakSet();
  return (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  };
}

function saveState(key, value) {
  try {
    // Use a replacer to handle circular references
    const json = JSON.stringify(value, getCircularReplacer());
    localStorage.setItem(key, json);
  } catch (error) {
    console.error('[saveState] Failed to save state:', error.message);
    // Don't throw - just log the error and continue
  }
}

/**
 * Track how many tabs/windows are open
 */
const activeTabKey = 'activeTabs';
function handlePageLoad() {
  const activeTabs = Number(localStorage.getItem(activeTabKey) ?? 0);
  localStorage.setItem(activeTabKey, activeTabs + 1);
}

function handleBeforeUnload() {
  const activeTabs = Number(localStorage.getItem(activeTabKey) ?? 0);
  if (activeTabs - 1 <= 0) {
    const workflowKeys = Object.keys(localStorage).filter((key) =>
      key.startsWith(workflowKeyPrefix),
    );
    workflowKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
  }

  localStorage.setItem(activeTabKey, activeTabs - 1 < 0 ? 0 : activeTabs - 1);
}
window.addEventListener('load', handlePageLoad);
window.addEventListener('beforeunload', handleBeforeUnload);
